import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, RotateCcw, ArrowRight } from "lucide-react";

import { Navbar } from "@/components/layout/Nav";
import { STORIES } from "@/data/stories";
import { useLang } from "@/context/LangContext";
import { Carlos } from "@/components/characters/Characters";
import { STORIES as TIDS } from "@/constants/testIds";

const StoryCard = ({ story, onPick, lang }) => (
  <div className="brutal p-6 bg-white h-full flex flex-col">
    <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
      #{story.id} · {story.topic}
    </div>
    <h3 className="mt-1 font-display font-black text-xl">{story.title[lang]}</h3>
    <p className="mt-3 font-body flex-1">{story.scene[lang]}</p>
    <button
      data-testid={TIDS.storyCard(story.id)}
      onClick={() => onPick(story)}
      className="mt-4 brutal-btn bg-canales-blue text-white px-4 py-2 self-start"
    >
      {lang === "es" ? "Jugar" : "Play"}
    </button>
  </div>
);

const StoryPlayer = ({ story, onClose, lang, t }) => {
  const [picked, setPicked] = useState(null);
  const outcome = picked ? story.choices.find((c) => c.id === picked) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="brutal bg-white max-w-2xl w-full p-6 sm:p-8 relative"
      >
        <button onClick={onClose} className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-canales-red text-white border-4 border-black font-display font-black">
          <X className="w-5 h-5 mx-auto" />
        </button>
        <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
          #{story.id} · {story.topic}
        </div>
        <h3 className="mt-1 font-display font-black text-2xl">{story.title[lang]}</h3>
        <p className="mt-3 font-body text-lg">{story.scene[lang]}</p>

        <div className="mt-6 grid gap-3">
          {story.choices.map((c, i) => (
            <button
              key={c.id}
              data-testid={TIDS.choice(i)}
              onClick={() => setPicked(c.id)}
              disabled={!!picked}
              className={`text-left brutal-sm p-4 font-body ${
                picked === c.id ? (c.correct ? "bg-green-100" : "bg-red-100") : "bg-canales-cream hover:bg-white"
              }`}
            >
              <span className="font-display font-bold mr-2">{c.id.toUpperCase()}.</span>
              {c.label[lang]}
            </button>
          ))}
        </div>

        <AnimatePresence>
          {outcome && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-6 brutal p-4 ${outcome.correct ? "bg-green-50" : "bg-red-50"} flex gap-3 items-start`}
            >
              <Carlos size={64} waving />
              <div>
                <div className={`font-display font-black text-lg ${outcome.correct ? "text-green-700" : "text-red-700"}`}>
                  {outcome.correct ? t("correct") : t("incorrect")}
                </div>
                <p className="font-body mt-1">{outcome.outcome[lang]}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setPicked(null)} className="brutal-btn bg-white px-3 py-1 text-sm flex items-center gap-1">
                    <RotateCcw className="w-4 h-4" /> {lang === "es" ? "Otra vez" : "Retry"}
                  </button>
                  <button onClick={onClose} className="brutal-btn bg-canales-blue text-white px-3 py-1 text-sm flex items-center gap-1">
                    {lang === "es" ? "Cerrar" : "Close"} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default function Stories() {
  const { t, lang } = useLang();
  const [active, setActive] = useState(null);
  const list = useMemo(() => STORIES, []);

  return (
    <div className="min-h-screen bg-halftone">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10" data-testid={TIDS.root}>
        <div className="mb-6">
          <h1 className="font-display font-black text-4xl">{t("stories")}</h1>
          <p className="font-body text-neutral-700">
            {lang === "es"
              ? "20 historias, tú eliges qué hacer. Carlos te dice si lo hiciste bien."
              : "20 stories, you decide what to do. Carlos tells you if you nailed it."}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((s) => (
            <StoryCard key={s.id} story={s} lang={lang} onPick={setActive} />
          ))}
        </div>

        {active && <StoryPlayer story={active} onClose={() => setActive(null)} lang={lang} t={t} />}
      </div>
    </div>
  );
}
